import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, untracked } from '@angular/core';
import { TableComponent } from '@ui/aos-lib';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { SidePanelComponent } from '../../../layout/components/side-panel/side-panel.component';
import { CustomersApiService } from './api/customers-api.service';
import type { CustomerDetailsForAPI } from './api/customers-api.type';
import type { CustomerForDetailsPage } from './customer-details-page/customer-details-page.type';
import { CustomerDevicesUtil } from './customer-devices/customer-devices.util';
import { Customer } from './customers.types';
import { convertToCustomerDetails } from './services/customers-service.util';
import { TableDevices } from '../../equipment/devices/components/devices.models';
import { ListingTableComponent } from '../../../shared/components/listing-table/listing-table.component';
import { notAvailable } from '@portal/shared/constants';
import {
  CustomerAssociatedDevice,
  CustomerConsumptionDevice,
  CustomerEventsDevice
} from './customer-devices/customer-devices.type';
import { ListingPageActions } from '@portal/shared/components/listing-page/store/listing-page.actions';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';

@Component({
  selector: 'aos-customer-listing-table',
  imports: [AsyncPipe, TableComponent, forwardRef(() => SidePanelComponent)],
  templateUrl: '../../../shared/components/listing-table/listing-table.component.html',
  styleUrl: '../../../shared/components/listing-table/listing-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomersListingTableComponent extends ListingTableComponent {
  private readonly customerApiService: CustomersApiService = inject(CustomersApiService);

  public override readonly deviceDetails$ = this.selectedRow$.pipe(
    filter((value: string | null): value is string => value !== null),
    switchMap(() => {
      const selectedRow = this.selectedRowBasicData() as Customer | null;
      if (!selectedRow) {
        return of([]);
      }

      const { customerOriginalNumber, customerSubNumber } = selectedRow;
      return this.customerApiService.getDetails(customerOriginalNumber, customerSubNumber).pipe(
        map<CustomerDetailsForAPI, CustomerForDetailsPage>(detailsFromAPI => {
          const customerDevicesUtil = new CustomerDevicesUtil(detailsFromAPI.associatedDevices);
          const customerDetails = convertToCustomerDetails(detailsFromAPI);
          const {
            customerFullNumber,
            customerNumber,
            customerStatus,
            customerSubNumber,
            customerType,
            email,
            fullName,
            phoneNumber
          } = customerDetails;
          return {
            associatedDevices: this.getDevicesWithZonedDates([...customerDevicesUtil.aggregateAssociatedDevices()]),
            consumptionDevices: this.getDevicesWithZonedDates([...customerDevicesUtil.aggregateConsumptionDevices()]),
            customerFullNumber,
            customerNumber,
            customerStatus,
            customerSubNumber,
            customerType,
            email,
            eventsDevices: this.getDevicesWithZonedDates([...customerDevicesUtil.aggregateEventsDevices()]),
            fullName,
            phoneNumber
          };
        }),
        map(details => (details ? [details] : null)),
        catchError(() => [])
      );
    })
  );

  readonly searchState = computed(() => this.pageState.state()?.search);

  private readonly onGridDataChange = effect(() => {
    const gridData = this.pageState.gridData();
    const isSearched = untracked(() => this.searchState()?.isSearched);

    if (!isSearched) return;

    this.store.dispatch(
      ListingPageActions.resetIsSearched({ pageId: this.pageState.model?.pageId as ListingPageId, isSearched: false })
    );

    if (gridData?.total === 1) {
      this.redirectToSingleDevice(gridData as TableDevices);
    }
  });

  constructor() {
    super();
  }

  private redirectToSingleDevice(customers: TableDevices): void {
    const customerUrlNumber = (customers.data[0] as Customer)?.customerUrlNumber;
    this.router.navigate([`customers/${customerUrlNumber}`]);
  }

  getDevicesWithZonedDates<T extends CustomerAssociatedDevice | CustomerConsumptionDevice | CustomerEventsDevice>(
    devices: T[]
  ): T[] {
    return devices.map(device => ({
      ...device,
      lastHeard: device.lastHeard ? this.timezoneService.formatLong(device.lastHeard) : device.lastHeard,
      dateLastAddedUTC: device.dateLastAddedUTC
        ? this.timezoneService.formatLong(device.dateLastAddedUTC)
        : notAvailable
    }));
  }
}
